// Takeoff Canvas 2.0 Track J / UI Phase A — the review queue, COMPUTED from
// existing state (TAKEOFF_CANVAS_2_ROADMAP.md Track J, UI brief §4 / §19,
// compatibility audit: "generate review items from existing state rather
// than adding a new business-object layer first"). Nothing here is
// persisted; every item points back at the record it came from so the dock
// can select / zoom / highlight it. What IS persisted (Track J, db/80
// TakeoffReviewStates) is the estimator's WORKFLOW on an item — acknowledged /
// waived / assigned / note — keyed by the item's stable id and laid over the
// computed queue by applyReviewStates(); an item's existence is never stored.

import { isOpening } from "../config/openings";

export const REVIEW_CATEGORIES = [
  { key: "proposals", label: "Proposals" },
  { key: "machine",   label: "Machine output" },
  { key: "scale",     label: "Scale" },
  { key: "geometry",  label: "Geometry" },
  { key: "rooms",     label: "Rooms" },
  { key: "openings",  label: "Openings" },
  { key: "elements",  label: "Elements" },
  { key: "pricing",   label: "Pricing" },
  { key: "layout",    label: "Layout" },
];

const placed = (r) => r && r.CanvasX != null && r.CanvasX !== "" && r.CanvasY != null && r.CanvasY !== "";

// Inputs are what the canvas already holds:
//   scale           { isSet, check }  — check = the session's last verdict (Track B) or null
//   hasDrawing      a sheet is loaded
//   wallExceptions  count of Review Walls joints (conflict / needs review / suggested / unassigned)
//   rooms           TakeoffRooms rows
//   conditions      drawn rows (openings are read from them)
//   elementWarnings [{ roomId, roomName, elementId, label, code, message }] from resolveElementQuantity
//   layoutConflicts [{ roomId, roomName, code, severity, message, conditionIds }] from layoutReview.reviewRoomLayout (Track H)
// Returns { items: [{ id, category, code, severity 'warn'|'info', message, roomId?, conditionIds? }], counts, total }
// overlaps       overlapCheck.findOverlaps() — [{ code, severity, roomId, conditionIds, message }]
// machineGroups  summarizeUnreviewed().groups (utils/machineReview.js) — generated symbols, rule-applied walls and
//                template-seeded elements awaiting a person; each item carries `accept` (the ids sp_AcceptMachineItems takes)
export function buildReviewQueue({ scale = {}, hasDrawing = false, wallExceptions = 0, rooms = [], conditions = [], elementWarnings = [], layoutConflicts = [], machineGroups = [], overlaps = [] } = {}) {
  const items = [];
  const add = (it) => items.push({ id: `${it.category}:${it.code}:${it.roomId ?? ""}:${(it.conditionIds || []).join(",")}:${it.elementId ?? ""}`, severity: "warn", ...it });

  if (hasDrawing) {
    if (!scale.isSet) add({ category: "scale", code: "scale_unset", message: "The drawing has no scale — set it before measuring anything." });
    else if (!scale.check) add({ category: "scale", code: "scale_unverified", severity: "info", message: "Scale not yet verified against a printed dimension this session." });
    else if (scale.check.verdict === "review") add({ category: "scale", code: "scale_review", message: `Scale is ${Math.round((scale.check.errorPct || 0) * 10) / 10}% off the printed dimension — review.` });
    else if (scale.check.verdict === "fail") add({ category: "scale", code: "scale_wrong", message: `Scale is ${Math.round((scale.check.errorPct || 0) * 10) / 10}% off the printed dimension — re-calibrate.` });
  }

  if (wallExceptions > 0) add({ category: "geometry", code: "wall_exceptions", message: `${wallExceptions} wall joint${wallExceptions === 1 ? "" : "s"} need${wallExceptions === 1 ? "s" : ""} a decision — see Review · Walls below.` });

  for (const r of rooms) {
    if (r.DetectionStatus === "proposed") {
      const c = r.DetectionConfidence == null ? null : Math.round(+r.DetectionConfidence * 100);
      add({ category: "proposals", code: "room_proposed", roomId: r.RoomID, accept: { rooms: [r.RoomID], conditions: [], elements: [] }, message: `${r.RoomName || `Room ${r.RoomID}`} is an AI proposal (${c == null ? "confidence not reported" : `${c}% confidence`}) — accept, edit or reject it.` });
    }
    if (!placed(r)) add({ category: "rooms", code: "room_unplaced", roomId: r.RoomID, message: `${r.RoomName || `Room ${r.RoomID}`} is in the schedule but not placed on the canvas.` });
    else if (r.RoomTemplateID == null) add({ category: "rooms", code: "room_no_template", severity: "info", roomId: r.RoomID, message: `${r.RoomName || `Room ${r.RoomID}`} has no room template — no default criteria or standard elements.` });
  }

  for (const c of conditions) {
    if (!isOpening(c)) continue;
    if (!c.Mark) add({ category: "openings", code: "opening_unmarked", severity: "info", roomId: c.RoomID ?? null, conditionIds: [c.TakeoffConditionDrawnID], message: `An opening in ${rooms.find(r => r.RoomID === c.RoomID)?.RoomName || "an unassigned room"} has no mark — it will not appear on the door / window schedule by name.` });
  }

  const seen = new Set();
  for (const w of elementWarnings) {
    const k = `${w.elementId}:${w.code}`;
    if (seen.has(k)) continue;
    seen.add(k);
    add({ category: w.category || "elements", code: w.code, roomId: w.roomId ?? null, elementId: w.elementId ?? null, conditionIds: w.conditionIds || [], severity: w.severity || "warn", message: `${w.roomName ? `${w.roomName} · ` : ""}${w.label ? `${w.label}: ` : ""}${w.message}` });
  }

  // Track H — HEPA / light layout conflicts (outside the room, crossing a wall,
  // overlapping, a light too close to a HEPA, spacing / uniformity).
  for (const c of layoutConflicts) {
    add({ category: "layout", code: c.code, severity: c.severity || "warn", roomId: c.roomId ?? null, conditionIds: c.conditionIds || [],
      message: `${c.roomName ? `${c.roomName} · ` : ""}${c.message}` });
  }

  // Cross-condition overlap check (utils/overlapCheck.js): the same thing measured twice on one drawing.
  for (const o of overlaps) {
    add({ category: "geometry", code: o.code, severity: o.severity || "warn", roomId: o.roomId ?? null, conditionIds: o.conditionIds || [], message: o.message });
  }

  // Review gate: machine output nobody has looked at yet. Still priced — this is visibility, not exclusion.
  for (const g of machineGroups) {
    add({ category: "machine", code: `machine_${g.kind}`, severity: "info", roomId: g.roomId ?? null, conditionIds: g.conditionIds || [], accept: g.accept,
      message: `${g.label} — not yet reviewed` });
  }

  const counts = Object.fromEntries(REVIEW_CATEGORIES.map(c => [c.key, items.filter(i => i.category === c.key).length]));
  return { items, counts, total: items.length, warnCount: items.filter(i => i.severity === "warn").length };
}

// ── Track J — durable workflow state over the computed queue ────────────────
export const REVIEW_STATUSES = Object.freeze({
  open:         { key: "open",         label: "Open" },
  acknowledged: { key: "acknowledged", label: "Acknowledged" },
  waived:       { key: "waived",       label: "Waived" },
});

const stateOf = (s) => ({ status: s?.Status === "waived" ? "waived" : s ? "acknowledged" : "open", assignedTo: s?.AssignedTo || null, note: s?.Note || null, updatedBy: s?.UpdatedBy || null, updatedAt: s?.UpdatedAt || null });

// states = sp_GetTakeoffReviewStates rows for the revision. Every item gets a
// `state` (no row = open); `visible` hides waived items unless showWaived;
// counts / total / warnCount describe the VISIBLE list (what the dock badge
// says); `resolved` = states whose item no longer computes — the cause was
// fixed since the estimator touched it. Derived every render, never stored.
export function applyReviewStates(queue, states = [], { showWaived = false } = {}) {
  const byKey = new Map((states || []).map(s => [s.ItemKey, s]));
  const items = queue.items.map(it => ({ ...it, state: stateOf(byKey.get(it.id)) }));
  const live = new Set(queue.items.map(i => i.id));
  const resolved = (states || []).filter(s => !live.has(s.ItemKey))
    .map(s => ({ id: s.ItemKey, category: s.Category, code: s.Code, message: s.Message, state: stateOf(s) }));
  const visible = showWaived ? items : items.filter(i => i.state.status !== "waived");
  const statusCounts = { open: 0, acknowledged: 0, waived: 0, assigned: 0 };
  for (const i of items) { statusCounts[i.state.status] += 1; if (i.state.assignedTo) statusCounts.assigned += 1; }
  const counts = Object.fromEntries(REVIEW_CATEGORIES.map(c => [c.key, visible.filter(i => i.category === c.key).length]));
  return { ...queue, items, visible, counts, total: visible.length, warnCount: visible.filter(i => i.severity === "warn").length, statusCounts, resolved };
}

// One sp_SaveTakeoffReviewState payload for one change on one item (the
// caller adds revision_id). The item's text is snapshotted so a later
// "resolved since review" row still says what it was about.
export function reviewStatePayload(item, patch, user) {
  return { item_key: item.id, category: item.category, code: item.code, message: item.message, updated_by: user || null, ...patch };
}
